import React from 'react';
import { Form, Switch, Space, Typography, Select, Input, theme } from 'antd';
import { RobotOutlined, MailOutlined, PhoneOutlined, MessageOutlined } from '@ant-design/icons';

const { Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

/**
 * AI Automated Creation Toggle
 * 
 * Allows users to enable AI automated creation via webhook
 * when creating an appointment. When enabled, the webhook will be
 * called with the appointment data to start AI agent assisted creation
 * instead of creating the appointment directly.
 */
export const AIAutomatedCreation: React.FC = () => {
  const { token } = theme.useToken();

  return (
    <Form.Item
      label={
        <Space size={4}>
          <RobotOutlined style={{ fontSize: token.fontSizeSM, color: token.colorPrimary }} />
          <span>AI Automated Creation</span>
        </Space>
      }
    >
      <Space orientation="vertical" size="small" style={{ width: '100%' }}>
        <Form.Item
          name="aiAutomatedCreation"
          valuePropName="checked"
          initialValue={false}
          style={{ marginBottom: 0 }}
        >
          <Switch
            checkedChildren="Enabled"
            unCheckedChildren="Disabled"
          />
        </Form.Item>
        
        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) => 
            prevValues.aiAutomatedCreation !== currentValues.aiAutomatedCreation
          }
        >
          {({ getFieldValue }) => {
            const isEnabled = getFieldValue('aiAutomatedCreation');
            
            if (!isEnabled) {
              return (
                <Text type="secondary" style={{ fontSize: token.fontSizeSM, display: 'block' }}>
                  An automated creation process with AI will be started with the applicant via email. 
                  The applicant will be asked to confirm the time slot and if it's convenient for them.
                </Text>
              );
            }

            return (
              <div style={{ 
                marginTop: token.marginXS,
                padding: token.paddingSM,
                background: token.colorFillAlter,
                borderRadius: token.borderRadius,
                border: `1px solid ${token.colorBorderSecondary}`
              }}>
                <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
                  <Text type="secondary" style={{ fontSize: token.fontSizeSM, display: 'block' }}>
                    An automated creation process with AI will be started with the applicant. 
                    The applicant will be asked to confirm the time slot and if it's convenient for them.
                  </Text>
                  
                  <Form.Item
                    name="aiCommunicationChannel"
                    label={
                      <Space size={4}>
                        <MessageOutlined style={{ fontSize: token.fontSizeSM }} />
                        <span style={{ fontSize: token.fontSizeSM }}>Communication Channel</span>
                      </Space>
                    }
                    initialValue="email"
                    style={{ marginBottom: token.marginXS }}
                  >
                    <Select
                      disabled
                      size="middle"
                      style={{ width: '100%' }}
                    >
                      <Option value="email">
                        <Space size={4}>
                          <MailOutlined />
                          <span>Email</span>
                        </Space>
                      </Option>
                      <Option value="phone">
                        <Space size={4}>
                          <PhoneOutlined />
                          <span>Phone</span>
                        </Space>
                      </Option>
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="aiAdditionalNotes"
                    label={
                      <span style={{ fontSize: token.fontSizeSM }}>Additional Notes for AI Agent</span>
                    }
                    style={{ marginBottom: 0 }}
                  >
                    <TextArea
                      rows={3}
                      placeholder="Add any specific instructions or context for the AI agent..."
                      style={{ fontSize: token.fontSizeSM }}
                      showCount
                      maxLength={500}
                    />
                  </Form.Item>
                </Space>
              </div>
            );
          }}
        </Form.Item>
      </Space>
    </Form.Item>
  );
};
